import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { JsonLd, breadcrumbSchema, webPageSchema } from "@/components/site/Schema";

// DRAFT — REQUIRES LEGAL/COMPLIANCE REVIEW BEFORE PUBLICATION.
// Review status is intentionally not shown prominently in the visitor UI; it is
// recorded here in code and via the data-review-status attribute below.
export default function LegalPage({ doc }) {
  return (
    <div
      className="min-h-screen bg-ivory"
      data-testid={`legal-page-${doc.slug}`}
      data-review-status="DRAFT — REQUIRES LEGAL/COMPLIANCE REVIEW BEFORE PUBLICATION"
    >
      <SEO title={doc.seoTitle} description={doc.seoDescription} />
      <JsonLd id={`ld-legal-breadcrumb-${doc.slug}`} data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: doc.title, path: `/${doc.slug}` }])} />
      <JsonLd id={`ld-legal-webpage-${doc.slug}`} data={webPageSchema({ path: `/${doc.slug}`, name: doc.title })} />
      <Header />
      <main>
        <section className="relative bg-ivory pt-[120px] md:pt-[150px] pb-12 md:pb-16 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 grid-lines opacity-50" />
          <div className="container-lr relative max-w-3xl">
            <div className="flex items-center gap-4">
              <span className="gold-rule" />
              <p className="eyebrow">{doc.eyebrow}</p>
            </div>
            <h1 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl sm:text-5xl leading-[1.06]">{doc.title}</h1>
            {doc.intro?.map((p, i) => (
              <p key={i} className="mt-6 text-slate text-base md:text-lg leading-relaxed">{p}</p>
            ))}
          </div>
        </section>

        <section className="bg-ivory pb-24 md:pb-32">
          <div className="container-lr max-w-3xl">
            <div className="space-y-10">
              {doc.sections.map((s) => (
                <div key={s.heading}>
                  <h2 className="font-serif text-xl md:text-2xl text-legacy leading-tight">{s.heading}</h2>
                  {s.paragraphs?.map((p, i) => (
                    <p key={i} className="mt-3 whitespace-pre-line text-slate text-[0.95rem] leading-relaxed">
                      {renderLegalText(p)}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="mt-4 space-y-2">
                      {s.list.map((item) => (
                        <li key={item} className="flex gap-3 text-slate text-[0.95rem] leading-relaxed">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-14 pt-8 border-t border-taupe/50">
              <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 text-bronze font-semibold text-sm hover:text-gold transition-colors">
                <ArrowLeft size={16} />Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}


const renderLegalText = (text) => {
  const tokens = text.split(/(shawn@legacylifeandretirement\.com|602-689-2606)/g);
  return tokens.map((token, index) => {
    if (token === "shawn@legacylifeandretirement.com") {
      return <a key={index} href="mailto:shawn@legacylifeandretirement.com" className="text-bronze underline hover:text-gold">{token}</a>;
    }
    if (token === "602-689-2606") {
      return <a key={index} href="tel:+16026892606" className="text-bronze underline hover:text-gold">{token}</a>;
    }
    return token;
  });
};
